import { useEffect, useMemo, useRef, useState } from "react";
import SearchInput from "./SearchInput";

const AREAS = [
  "Indoor",
  "Outdoor",
  "Pool Area Dining",
  "Garden Dining",
  "Rooftop",
  "Terrace",
  "VIP Section",
  "Private Dining",
  "Bar Area",
  "Family Section",
  "Lounge",
  "Banquet Hall",
];

interface Props {
  open: boolean;
  onClose: () => void;
  selected: string[];
  onChange: (next: string[]) => void;
  anchorRef?: React.RefObject<HTMLElement | null>;
}

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M5 12l5 5L20 7" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SelectAreaPopup = ({ open, onClose, selected, onChange, anchorRef }: Props) => {
  const [query, setQuery] = useState("");
  const popRef = useRef<HTMLDivElement>(null);
  const filtered = useMemo(() => AREAS.filter((a) => a.toLowerCase().includes(query.toLowerCase())), [query]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popRef.current?.contains(target)) return;
      if (anchorRef?.current?.contains(target)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  const toggle = (area: string) => {
    if (selected.includes(area)) onChange(selected.filter((a) => a !== area));
    else onChange([...selected, area]);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center" onClick={onClose} >
      <div className="absolute inset-0 backdrop-blur-sm" style={{ backgroundColor: "rgba(0,0,0,0.7)" }} />
      <div
        ref={popRef}
        role="dialog"
        aria-label="Select Area"
        className="relative w-full max-w-[430px] bg-white rounded-t-[20px] shadow-[0_-4px_20px_rgba(0,0,0,0.18)] overflow-hidden max-h-[80vh] flex flex-col select-area-sheet"
        onClick={(e) => e.stopPropagation()}
      >
        <style>
          {`@keyframes selectAreaSlideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
            .select-area-sheet { animation: selectAreaSlideUp 240ms ease-out; }`}
        </style>

        {/* Drag handle */}
        <div className="flex justify-center pt-2 pb-1">
          <span className="block w-10 h-1 rounded-full bg-[#E8E9F1]" />
        </div>

        <div className="px-4 pt-2 pb-2 flex items-center justify-between">
          <h2 className="font-sansation font-bold text-[#111] text-[16px]">
            Select Area
          </h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center bg-transparent border-0 p-0"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M1 1l12 12M13 1L1 13" stroke="#111" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="px-4 pt-1 pb-2">
          <SearchInput value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search Area" />
        </div>

        <ul className="px-4 pb-6 pt-2 overflow-y-auto flex flex-col gap-3">
          {filtered.length === 0 && (
            <li className="font-opensans text-[14px] text-[#8F9098] py-2">
              No areas found
            </li>
          )}
          {filtered.map((area) => {
            const isChecked = selected.includes(area);
            return (
              <li key={area}>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <span className="w-6 h-6 rounded-[4px] border-2 flex items-center justify-center transition"
                    style={{ backgroundColor: isChecked ? "#F15A29" : "#FFFFFF", borderColor: isChecked ? "#F15A29" : "#C5C6CC" }}
                  >
                    {isChecked && <CheckIcon />}
                  </span>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggle(area)}
                    className="sr-only"
                  />
                  <span className="font-opensans text-[14px] text-[#0E0809]">
                    {area}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default SelectAreaPopup;
